import { useState } from "react";
import { Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { AppHeader, PageShell } from "@/components/AppHeader";
import { SiteFooter } from "@/components/SiteFooter";

const Legal = ({ title, testId, children }) => (
  <PageShell>
    <AppHeader />
    <article className="card max-w-3xl !p-6 sm:!p-8 legal" data-testid={testId}>
      <h1 className="font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">{title}</h1>
      <p className="text-sm text-[var(--muted)]">Last updated June 2026</p>
      {children}
    </article>
    <SiteFooter />
  </PageShell>
);

export const PrivacyPage = () => (
  <Legal title="Privacy" testId="privacy-page">
    <h2>What we collect</h2>
    <ul>
      <li>Coach accounts: name, email, and a hashed password (or your Google profile if you sign in with Google).</li>
      <li>Team data: team names, custom plays and notes you create.</li>
      <li>Practice: the first name a player enters and their practice answers. No email, birthday or other details from players.</li>
      <li>Payments are handled by Stripe. We never see or store card numbers.</li>
    </ul>
    <h2>How we use it</h2>
    <p>Only to run DiamondPlay: show your playbook, track practice for your team, and manage your plan. We don't sell data or show ads.</p>
    <h2>Kids</h2>
    <p>Players don't create accounts. Coaches control what their team sees, and can delete a team (and its practice data) at any time.</p>
    <h2>Your choices</h2>
    <p>Ask us to export or delete your account data through the Contact page.</p>
  </Legal>
);

export const TermsPage = () => (
  <Legal title="Terms" testId="terms-page">
    <h2>Using DiamondPlay</h2>
    <p>DiamondPlay is a teaching aid for youth baseball and softball. Default plays are a starting point, so review them with your league's rules and your coaching judgment.</p>
    <h2>Accounts</h2>
    <p>Keep your login safe. You're responsible for the content you add to your team playbook and for sharing your team link with the right people.</p>
    <h2>Plans and billing</h2>
    <ul>
      <li>New coaches get a 14-day free trial. No card is required.</li>
      <li>The Team plan is $9.99/month or $79/year, billed through Stripe, and renews until canceled.</li>
      <li>Cancel any time. Access continues until the end of the paid period.</li>
    </ul>
    <h2>Fair use</h2>
    <p>Don't misuse the service, try to break it, or upload anything unlawful. We may suspend accounts that do.</p>
    <h2>No warranty</h2>
    <p>The service is provided as is. We're not liable for game outcomes, injuries, or lost data beyond what the law requires.</p>
  </Legal>
);

export const ContactPage = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [state, setState] = useState({ busy: false, sent: false, error: "" });
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, sent: false, error: "" });
    try {
      await api.contact(form);
      setState({ busy: false, sent: true, error: "" });
    } catch (err) {
      setState({ busy: false, sent: false, error: formatApiError(err) });
    }
  };
  return (
    <PageShell>
      <AppHeader />
      <div className="card max-w-xl !p-6 sm:!p-8 space-y-4" data-testid="contact-page">
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold uppercase tracking-tight">Contact</h1>
        {state.sent ? (
          <p className="font-bold text-lg" data-testid="contact-success">Thanks! We got your message and will reply by email.</p>
        ) : (
          <form className="space-y-3" onSubmit={submit}>
            <p className="font-medium text-[var(--muted)]">Questions, feature ideas, or a play that looks wrong? Tell us.</p>
            <input data-testid="contact-name-input" className="field-input w-full" placeholder="Your name" required maxLength={80} value={form.name} onChange={set("name")} />
            <input data-testid="contact-email-input" type="email" className="field-input w-full" placeholder="Email" required value={form.email} onChange={set("email")} />
            <textarea data-testid="contact-message-input" className="field-input w-full min-h-[140px]" placeholder="Message" required minLength={5} maxLength={2000} value={form.message} onChange={set("message")} />
            {state.error && <p className="text-sm font-bold text-red-600" data-testid="contact-error">{state.error}</p>}
            <button className="btn-primary w-full" data-testid="contact-submit-button" disabled={state.busy}>{state.busy && <Loader2 size={18} className="animate-spin" />} Send message</button>
          </form>
        )}
      </div>
      <SiteFooter />
    </PageShell>
  );
};
